'use client';

/**
 * 我的待办页（议程项资料上传任务）
 * 路由：/meetingattendance/my-tasks
 * 权限：登录即可（后端按 currentUser 过滤）
 */

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { Calendar, Inbox, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import {
  uploadTaskApi,
  type MyUploadTask,
  type MyUploadTasksQuery,
} from '@/services/api/upload-task';
import type { UploadTaskStatus } from '@/services/api/agenda';
import { formatMeetingDateTime } from '@/features/meeting-attendance/utils/date';

type StatusFilter = UploadTaskStatus | 'ALL';
type SortOption = 'dueAt_asc' | 'assignedAt_desc';

const PAGE_LIMIT = 20;

export default function MyTasksPage() {
  useAuthGuard();
  const { t } = useTranslation();
  const ta = t.meetingAttendance;

  const [status, setStatus] = useState<StatusFilter>('PENDING');
  const [sort, setSort] = useState<SortOption>('dueAt_asc');
  const [items, setItems] = useState<MyUploadTask[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchList = useCallback(
    async (append: boolean, cursorParam?: string) => {
      const query: MyUploadTasksQuery = {
        status,
        sort,
        limit: PAGE_LIMIT,
        cursor: cursorParam,
      };
      if (append) setLoadMoreLoading(true);
      else setLoading(true);
      setLoadError(null);
      try {
        const resp = await uploadTaskApi.listMyUploadTasks(query);
        setItems((prev) => (append ? [...prev, ...resp.items] : resp.items));
        setNextCursor(resp.nextCursor);
      } catch (err) {
        setLoadError(ta.myTasks.loadFailed);
      } finally {
        if (append) setLoadMoreLoading(false);
        else setLoading(false);
      }
    },
    [status, sort, ta.myTasks.loadFailed],
  );

  useEffect(() => {
    setCursor(undefined);
    fetchList(false, undefined);
  }, [fetchList]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{ta.myTasks.pageTitle}</h1>
          <p className="mt-1 text-sm text-gray-500">{ta.myTasks.pageSubtitle}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{ta.myTasks.pageTitle}</span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">{ta.myTasks.filterStatusLabel}</span>
                <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
                  <SelectTrigger className="h-8 w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING">{ta.myTasks.filterStatusPending}</SelectItem>
                    <SelectItem value="UPLOADED">{ta.myTasks.filterStatusUploaded}</SelectItem>
                    <SelectItem value="CANCELLED">{ta.myTasks.filterStatusCancelled}</SelectItem>
                    <SelectItem value="ALL">{ta.myTasks.filterStatusAll}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">{ta.myTasks.sortLabel}</span>
                <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
                  <SelectTrigger className="h-8 w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dueAt_asc">{ta.myTasks.sortDueAtAsc}</SelectItem>
                    <SelectItem value="assignedAt_desc">{ta.myTasks.sortAssignedAtDesc}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {ta.common.loading}
            </div>
          ) : loadError ? (
            <div className="py-12 text-center">
              <div className="text-sm text-red-600">{loadError}</div>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => fetchList(false)}>
                {ta.myTasks.loadRetry}
              </Button>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Inbox className="h-12 w-12" />
              <div className="mt-3 text-sm">{ta.myTasks.empty}</div>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((task) => {
                const statusBadge = renderStatusBadge(task.status, ta);
                return (
                  <li
                    key={task.id}
                    className="rounded-lg border border-gray-200 bg-white p-4 hover:border-blue-300"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">
                          {ta.myTasks.cardTitle
                            .replace('{meeting}', task.agendaItem.meeting.title)
                            .replace('{item}', task.agendaItem.title)}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {ta.myTasks.cardSection}: {task.agendaItem.sectionTitle}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                          <span className="inline-flex items-center">
                            <Calendar className="mr-1 h-3 w-3" />
                            {task.dueAt
                              ? ta.myTasks.cardDueAt.replace('{date}', formatMeetingDateTime(task.dueAt))
                              : ta.myTasks.cardNoDueAt}
                          </span>
                          <span>
                            {ta.myTasks.cardAssignedBy.replace(
                              '{name}',
                              task.assignedBy?.displayName || '-',
                            )}
                          </span>
                          {statusBadge}
                        </div>
                      </div>
                      <Link
                        href={`/meetingattendance/meetings/${task.agendaItem.meeting.id}#agenda-item-${task.agendaItem.id}`}
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={task.status !== 'PENDING'}
                        >
                          {ta.myTasks.cardGoUpload}
                        </Button>
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {nextCursor && !loading && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={loadMoreLoading}
                onClick={() => fetchList(true, nextCursor)}
              >
                {loadMoreLoading ? (
                  <>
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    {ta.common.loading}
                  </>
                ) : (
                  ta.myTasks.loadMore
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function renderStatusBadge(s: UploadTaskStatus, ta: any) {
  const map: Record<UploadTaskStatus, { label: string; cls: string }> = {
    PENDING: {
      label: ta.agenda.uploadTask.status.pending,
      cls: 'bg-amber-100 text-amber-800',
    },
    UPLOADED: {
      label: ta.agenda.uploadTask.status.uploaded,
      cls: 'bg-green-100 text-green-800',
    },
    CANCELLED: {
      label: ta.agenda.uploadTask.status.cancelled,
      cls: 'bg-gray-200 text-gray-700',
    },
  };
  const c = map[s];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${c.cls}`}>
      {c.label}
    </span>
  );
}
